import React from "react";
import { Box, Typography, Grid, Card, Button, Paper, Container } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import CountUp from "react-countup";
import { WorkOutline, Groups, School, Business, Search, Brush, Code, RocketLaunch } from "@mui/icons-material";
import { motion } from "framer-motion";

// Internship & Workshop images
import internshipImage from "../images/internship_meeting.png";
import workshopImage from "../images/workshop_new.jpg";
import southIndianWeddingImg from "../images/south_indian_wedding.jpg"; // New Local Image for Kongu Wedding
import smartsyncLogo from "../images/smartsync_logo.png";

import aiImage from "../images/services/ai_new_service.png"; // Custom AI Image
import graphicDesignImage from "../images/services/graphic_design_custom.png"; // Custom Graphic Design Image
import cloudImage from "../images/services/cloud_custom.png"; // Custom Cloud Image
import digitalMarketingImage from "../images/services/digital_marketing_custom.png"; // Custom Digital Marketing Image

// Service images (Curated High-Quality Tech Visuals)
const webImage = "https://images.unsplash.com/photo-1555099962-4199c345e5dd?q=80&w=1000&auto=format&fit=crop"; // Code on screen, dark



const dataAnalyticsImage = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1000&auto=format&fit=crop"; // Data visualization dashboard
const cybersecurityImage = "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1000&auto=format&fit=crop"; // Cyber lock background
const appDevelopmentImage = "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=1000&auto=format&fit=crop"; // Mobile phone app
const iotImage = "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1000&auto=format&fit=crop"; // Chip/Circuit board

const blockchainImage = "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=1000&auto=format&fit=crop"; // Blockchain blocks/nodes



const MotionBox = motion.create(Box);

const servicesData = [
  {
    title: "Web Development",
    tagline: "Digital Sovereignty",
    image: webImage,
    path: "/services/web-development",
    desc: "Architecting high-performance, scalable web ecosystems using React, Next.js, and Micro-Frontends for superior user engagement.",
    color: "#00E5FF" // Cyan
  },
  {
    title: "Artificial Intelligence",
    tagline: "Cognitive Automation",
    image: aiImage,
    path: "/services/artificial-intelligence",
    desc: "Driving operational efficiency with Generative AI, LLMs, and predictive models that turn data into strategic foresight.",
    color: "#E040FB" // Purple
  },
  {
    title: "Data Analytics",
    tagline: "Strategic Intelligence",
    image: dataAnalyticsImage,
    path: "/services/data-analytics",
    desc: "Transforming raw information into decisive business intelligence through advanced visualization and big data pipelines.",
    color: "#2979FF" // Blue
  },
  {
    title: "Digital Marketing",
    tagline: "Market Dominance",
    image: digitalMarketingImage,
    path: "/services/digital-marketing",
    desc: "Elevating brand authority with data-driven SEO, programmatic advertising, and omnichannel content strategies.",
    color: "#FF4081" // Pink
  },
  {
    title: "Cybersecurity",
    tagline: "Enterprise Defense",
    image: cybersecurityImage,
    path: "/services/cybersecurity",
    desc: "Safeguarding digital perimeters with Zero Trust architectures, real-time threat hunting, and compliance auditing.",
    color: "#FF3D00" // Deep Orange
  },
  {
    title: "Graphic Design",
    tagline: "Visual Identity",
    image: graphicDesignImage,
    path: "/services/graphic-design",
    desc: "Forging compelling brand narratives through premium UI/UX design, motion graphics, and corporate identity systems.",
    color: "#00C853" // Green
  },
  {
    title: "App Development",
    tagline: "Mobile-First Innovation",
    image: appDevelopmentImage,
    path: "/services/app-development",
    desc: "Engineering native and cross-platform mobile solutions that define the next generation of user mobility.",
    color: "#FFD740" // Amber
  },
  {
    title: "IoT & Embedded Systems",
    tagline: "Connected Ecosystems",
    image: iotImage,
    path: "/services/iot-embedded",
    desc: "Bridging physical and digital worlds with industrial IoT (IIoT) solutions and smart sensor networks.",
    color: "#7C4DFF" // Deep Purple
  },
  {
    title: "Cloud Computing",
    tagline: "Cloud-Native Scale",
    image: cloudImage,
    path: "/services/cloud-computing",
    desc: "Optimizing infrastructure agility with serverless computing, multi-cloud orchestration, and DevOps automation.",
    color: "#40C4FF" // Light Blue
  },
  {
    title: "Blockchain Technology",
    tagline: "Decentralized Trust",
    image: blockchainImage,
    path: "/services/blockchain",
    desc: "Implementing secure, transparent distributed ledger technologies for supply chain, finance, and identity management.",
    color: "#69F0AE" // Light Green
  },
];

const processSteps = [
  { title: "Discovery & Planning", icon: <Search fontSize="large" />, desc: "We analyze your requirements, market trends, and business goals to chart the perfect roadmap." },
  { title: "UI/UX Design", icon: <Brush fontSize="large" />, desc: "Our designers create intuitive, user-centric interfaces that ensure seamless navigation and engagement." },
  { title: "Agile Development", icon: <Code fontSize="large" />, desc: "We build robust, scalable solutions using iterative sprints, ensuring flexibility and code quality." },
  { title: "Testing & Deployment", icon: <RocketLaunch fontSize="large" />, desc: "Rigorous QA testing guarantees a bug-free launch, followed by comprehensive support." }
];

const SectionHeader = ({ tagline, title, subtitle, center = false }) => (
  <Box mb={{ xs: 5, md: 6 }} textAlign={center ? "center" : "left"}>
    <Typography variant="overline" sx={{ color: '#1A9E7F', letterSpacing: '0.2em', fontWeight: 700 }}>
      {tagline}
    </Typography>
    <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, color: '#034522', fontFamily: '"Lato", sans-serif', fontSize: { xs: '2rem', md: '3rem' } }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="h6" sx={{ mt: 2, color: '#333', maxWidth: '700px', mx: center ? 'auto' : 0, lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

function Services() {
  const navigate = useNavigate();

  const handleCardClick = (path) => navigate(path);
  const handleApplyNow = () =>
    window.open("https://forms.gle/VWbfxxMatLwjXJxo9", "_blank");
  const handleEnrollNow = () =>
    window.open("https://forms.gle/y23rpEYpSW1EnTye7", "_blank");

  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: "#fff" }}>

      {/* 🔹 HERO SECTION */}
      <Box sx={{ bgcolor: '#002613', color: 'white', pt: { xs: 15, md: 20 }, pb: { xs: 8, md: 15 }, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
        <Box sx={{ position: 'absolute', top: '-50%', left: '-10%', width: '800px', height: '800px', background: 'radial-gradient(circle, rgba(255,154,21,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography variant="overline" sx={{ color: '#1A9E7F', letterSpacing: 3, fontWeight: 700 }}>Our Core Competency</Typography>
            <Typography variant="h2" fontWeight={800} sx={{ mt: 2, mb: 4, fontFamily: '"Lato", sans-serif', fontSize: { xs: '2.5rem', md: '3.75rem' }, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              Architecting the Agile Enterprise
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto', lineHeight: 1.8, fontWeight: 300, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              From robust enterprise platforms to cutting-edge AI integrations, we deliver a full spectrum of strategic technology services designed to future-proof your business.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* 🔹 SERVICES GRID */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box textAlign="center" mb={10}>
          <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Our Capabilities</Typography>
          <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>Domains of Excellence</Typography>
          <Typography variant="h6" sx={{ color: '#333', mt: 2, fontWeight: 400, maxWidth: '700px', mx: 'auto' }}>
            We specialize in delivering high-impact solutions across the most critical technology sectors.
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {servicesData.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  onClick={() => handleCardClick(service.path)}
                  elevation={0}
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: '1px solid #eee',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${service.color}20`,
                      borderColor: service.color
                    }
                  }}
                >
                  <Box sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fafafa', height: '240px', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={service.image}
                      alt={service.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: 'block'
                      }}
                    />
                  </Box>

                  {/* Content - Static Stacked Layout */}
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: "white",
                      borderTop: '1px solid #f0f0f0',
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between" // Ensure button is at bottom if we set minHeight
                    }}
                  >
                    <Box>
                      <Typography variant="overline" sx={{ color: service.color, fontWeight: 800, letterSpacing: 1.5, fontSize: '0.75rem' }}>
                        {service.tagline}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#034522",
                          fontWeight: 800,
                          fontSize: "1.25rem",
                          mb: 1.5,
                          mt: 0.5,
                          lineHeight: 1.3
                        }}
                      >
                        {service.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#333",
                          lineHeight: 1.6,
                          mb: 2,
                          fontWeight: 500
                        }}
                      >
                        {service.desc}
                      </Typography>
                    </Box>

                    <Button
                      variant="text"
                      sx={{
                        color: service.color,
                        p: 0,
                        justifyContent: 'flex-start',
                        fontWeight: 700,
                        textTransform: 'none',
                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" }
                      }}
                    >
                      Learn More &rarr;
                    </Button>
                  </Box>
                </Card>
              </MotionBox>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* 🔹 FLAGSHIP PLATFORM - SMARTSYNC ERP */}
      <Box sx={{ py: { xs: 8, md: 15 }, background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', position: 'relative', overflow: 'hidden', color: 'white' }}>
        <Box sx={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)', borderRadius: '50%' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', inset: -20, bgcolor: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', zIndex: 0 }} />
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 6,
                      bgcolor: 'white',
                      position: 'relative',
                      zIndex: 1,
                      textAlign: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    <Box component="img" src={smartsyncLogo} sx={{ width: '60%', maxWidth: '200px', mb: 2 }} />
                    <Box 
                      component="img" 
                      src={require('../images/smartsync_dashboard.png')} 
                      sx={{ width: '100%', borderRadius: 2, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }} 
                    />
                  </Paper>
                  
                  <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                     <Paper sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', flex: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={800} color="#fbcfe8">99.9%</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>SYSTEM UPTIME</Typography>
                     </Paper>
                     <Paper sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', flex: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={800} color="#c084fc">256-bit</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>ENCRYPTION</Typography>
                     </Paper>
                  </Box>
                </Box>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Typography variant="overline" fontWeight={800} color="#a5b4fc" letterSpacing={2}>
                  ENTERPRISE RESOURCE PLANNING
                </Typography>
                <Typography variant="h2" fontWeight={900} sx={{ color: 'white', mt: 1, mb: 3, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                  SmartSync ERP
                </Typography>
                <Typography paragraph sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
                  Our flagship ERP solution is a masterclass in modern engineering. Designed to bridge the gap between complex data and intuitive decision-making, it empowers organizations with real-time analytics and seamless workflow automation.
                </Typography>

                <Box sx={{ mb: 4 }}>
                  {[
                    { title: "Admin Control Center", features: ["Real-time Analytics", "Lifecycle Mgmt", "Task Orchestration"] },
                    { title: "Employee Portal", features: ["Self-Service Dashboard", "Smart Attendance", "Collaboration"] },
                    { title: "Unified Ecosystem", features: ["Data Fluidity", "Instant Sync", "Cross-Platform Access"] }
                  ].map((item, i) => (
                    <Box key={i} display="flex" gap={2} mb={2.5} alignItems="flex-start">
                      <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', bgcolor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1rem', fontWeight: 900 }}>
                        ✓
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800} color="white" lineHeight={1.2}>{item.title}</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {item.features.map((f, j) => (
                            <Typography key={j} variant="caption" sx={{ opacity: 0.6 }}>{f}{j < item.features.length - 1 ? ' • ' : ''}</Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Button
                  component="a"
                  href="https://smart-sync-jp6b4noav-growaitechs-projects.vercel.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: '#6366f1',
                    color: 'white',
                    fontWeight: 800,
                    borderRadius: 50,
                    px: 6,
                    py: 2,
                    boxShadow: '0 10px 20px rgba(99, 102, 241, 0.3)',
                    '&:hover': { bgcolor: '#4f46e5', transform: 'translateY(-2px)' }
                  }}
                >
                  Get Custom Demo
                </Button>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 FEATURED PLATFORM - KONGU KALYANAM */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#FFF3E0', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, bgcolor: '#FFE0B2', borderRadius: '50%', opacity: 0.5 }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 6,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(230, 81, 0, 0.15)',
                    transform: 'rotate(-1deg)',
                    transition: '0.3s',
                    '&:hover': { transform: 'rotate(0)' }
                  }}
                >
                  <Box
                    component="img"
                    src={southIndianWeddingImg}
                    alt="South Indian Kongu Wedding Couple"
                    sx={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </Paper>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Typography variant="overline" fontWeight={800} color="#E65100" letterSpacing={2}>
                  LIVE PRODUCT SHOWCASE
                </Typography>
                <Typography variant="h2" fontWeight={800} sx={{ color: '#BF360C', mt: 1, mb: 3 }}>
                  Kongu Kalyanam App
                </Typography>
                <Typography paragraph sx={{ color: '#5D4037', fontSize: '1.1rem', lineHeight: 1.8 }}>
                  A premier community matrimony platform engineered for trust and privacy. We built a complete ecosystem featuring a high-speed React web portal, a native-performance Flutter mobile app, and a robust Firebase backend.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {[
                    { label: "Real-time Chat", val: "Firebase" },
                    { label: "Cross-Platform", val: "Flutter" },
                    { label: "Secure Database", val: "Firestore" },
                    { label: "Responsive UI", val: "React" }
                  ].map((item, i) => (
                    <Grid item xs={6} sm={3} key={i}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 3, textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="caption" display="block" color="text.secondary" fontWeight={600}>{item.label}</Typography>
                        <Typography variant="subtitle2" fontWeight={800} color="#E65100">{item.val}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.kongukalyanam.app"
                  target="_blank"
                  variant="contained"
                  size="large"
                  startIcon={<RocketLaunch />}
                  sx={{
                    bgcolor: '#E65100',
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: 50,
                    px: 4,
                    py: 1.5,
                    '&:hover': { bgcolor: '#BF360C' }
                  }}
                >
                  View on Play Store
                </Button>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 CLIENT SUCCESS STORIES - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#002613', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="overline" sx={{ color: '#69F0AE', letterSpacing: 2, fontWeight: 700 }}>Case Studies</Typography>
            <Typography variant="h3" fontWeight={800} sx={{ mt: 1 }}>Delivering Real Results</Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              {
                client: "Global FinTech Corp",
                title: "Modernizing Payment Infrastructure",
                res: "40% Faster Transactions",
                desc: "Migrated a legacy monolith to a microservices architecture using Node.js and Kubernetes, enabling zero-downtime deployments."
              },
              {
                client: "HealthPlus AI",
                title: "Predictive Analytics for Patient Care",
                res: "95% Diagnosis Accuracy",
                desc: "Developed a HIPAA-compliant AI model to assist radiologists in early detection of anomalies using Computer Vision."
              },
              {
                client: "EcoRetail",
                title: "Scalable E-Commerce Platform",
                res: "3x Traffic Handling",
                desc: "Built a headless commerce solution with Next.js and Shopify, supporting millions of concurrent users during peak sales."
              }
            ].map((caseStudy, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, height: '100%', border: '1px solid rgba(255,255,255,0.1)', transition: '0.3s', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Typography variant="overline" color="#69F0AE" fontWeight={700}>{caseStudy.client}</Typography>
                  <Typography variant="h5" fontWeight={700} my={2} color="white">{caseStudy.title}</Typography>
                  <Typography variant="h4" fontWeight={800} color="#1A9E7F" mb={2}>{caseStudy.res}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>{caseStudy.desc}</Typography>
                  <Button component={Link} to="/contact" variant="outlined" sx={{ mt: 3, color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 50, px: 3, '&:hover': { borderColor: '#69F0AE', color: '#69F0AE' } }}>
                    Schedule Consultation
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 OUR PROCESS (New Section) */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Methodology</Typography>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>How We Deliver Excellence</Typography>
          </Box>
          <Grid container spacing={6}>
            {processSteps.map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <MotionBox
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  textAlign="center"
                >
                  <Box sx={{
                    width: 80, height: 80,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    border: '2px solid #1A9E7F',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 3, color: '#1A9E7F',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.05)'
                  }}>
                    {step.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{step.desc}</Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 STRATEGIC ALLIANCES - ECOSYSTEM */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Ecosystem" title="Strategic Alliances" subtitle="We collaborate with the world's leading technology providers to build robust, future-proof solutions." center />
          <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
            {[
              { name: "AWS Partner", type: "Cloud Infrastructure", color: "#FF9900" },
              { name: "Microsoft Solutions", type: "Enterprise Software", color: "#00A4EF" },
              { name: "Google Cloud", type: "Data & AI", color: "#4285F4" },
              { name: "Salesforce", type: "CRM Integration", color: "#00A1E0" },
              { name: "MongoDB", type: "Database Modernization", color: "#47A248" },
              { name: "Shopify Plus", type: "E-Commerce Scale", color: "#96BF48" }
            ].map((partner, i) => (
              <Grid item xs={6} md={2} key={i}>
                <Box sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#FAFAFA', borderRadius: 2, transition: '0.3s', '&:hover': { bgcolor: 'white', boxShadow: '0 10px 20px rgba(0,0,0,0.05)', transform: 'translateY(-5px)' } }}>
                  <Typography variant="h6" fontWeight={800} sx={{ color: partner.color, fontSize: '1rem', mb: 1 }}>{partner.name}</Typography>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>{partner.type}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 QUALITY ASSURANCE FRAMEWORK */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FAFAFA' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionHeader tagline="Excellence Guaranteed" title="The GAT Quality Promise" subtitle="We don't just write code; we engineer reliability. Our rigorous Quality Assurance framework ensures that every deliverable meets the highest global standards." />
              <Box sx={{ mt: 4 }}>
                {[
                  { title: "Automated Testing Suites", desc: "100% code coverage with CI/CD integrated unit and integration tests." },
                  { title: "Security Vulnerability Scans", desc: "OWASP Top 10 compliance checks and automated penetration testing." },
                  { title: "Performance Engineering", desc: "Load testing to ensure your application handles millions of requests seamlessly." },
                  { title: "Accessibility Compliance", desc: "WCAG 2.1 AA standard adherence for inclusive digital experiences." }
                ].map((qa, i) => (
                  <Box key={i} sx={{ mb: 4, pl: 3, borderLeft: '4px solid #1A9E7F' }}>
                    <Typography variant="h6" fontWeight={700} color="#034522">{qa.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{qa.desc}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 5, borderRadius: 8, bgcolor: '#002613', color: 'white', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, right: 0, width: 200, height: 200, bgcolor: '#69F0AE', opacity: 0.1, borderRadius: '0 0 0 100%' }} />
                <Typography variant="h3" fontWeight={800} mb={2}>Zero</Typography>
                <Typography variant="h5" sx={{ opacity: 0.8, mb: 6 }}>Critical Defects Post-Launch</Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 4 }}>
                  "Our rigorous testing protocols allowed us to launch our Fintech platform without a single critical bug, handling $10M in transactions on Day 1."
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 40, height: 40, bgcolor: '#69F0AE', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#002613', fontWeight: 900 }}>✓</Box>
                  <Typography variant="subtitle1" fontWeight={700}>ISO 9001 Certified Process</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 MANAGED IT SERVICES - PROFESSIONAL */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Operational Excellence</Typography>
              <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1, mb: 3 }}>Comprehensive Managed Services</Typography>
              <Typography paragraph sx={{ color: '#555', fontSize: '1.1rem', lineHeight: 1.8 }}>
                Achieve uninterrupted business continuity with our tailored managed IT solutions. We assume full responsibility for your infrastructure, security, and maintenance—empowering you to focus on high-value strategic initiatives.
              </Typography>
              <Box mt={4}>
                {[
                  { title: "Proactive Resolution", desc: "Advanced monitoring systems that identify and neutralize risks before operational impact." },
                  { title: "Enterprise Security", desc: "Multi-layered defense protocols including zero-trust architecture and 24/7 threat hunting." },
                  { title: "Continuous Optimization", desc: "Regular infrastructure audits to enhance performance, scalability, and cost-efficiency." }
                ].map((item, i) => (
                  <Box key={i} display="flex" gap={2} mb={3}>
                    <Box sx={{ minWidth: 40, height: 40, borderRadius: '50%', bgcolor: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1A9E7F' }}>
                      <WorkOutline fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color="#034522">{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={4}>
                {[
                  { plan: "Essential Operations", price: "Startup Scale", features: ["9-5 Support", "Basic Infrastructure Monitoring", "Monthly Performance Reports", "Patch Management"] },
                  { plan: "Proactive Growth", price: "Growth Scale", features: ["24/7 Support Desk", "Real-Time Threat Detection", "Weekly Optimization Reports", "Priority Incident Response"], active: true },
                  { plan: "Strategic Enterprise", price: "Global Scale", features: ["Dedicated DevOps Team", "Custom SLA & Uptime Guarantees", "Quarterly Architecture Review", "On-site Support Options"] }
                ].map((pkg, i) => (
                  <Grid item xs={12} sm={4} key={i}>
                    <Paper
                      elevation={pkg.active ? 10 : 1}
                      sx={{
                        p: 4,
                        borderRadius: 4,
                        textAlign: 'center',
                        position: 'relative',
                        bgcolor: pkg.active ? '#034522' : '#fff',
                        color: pkg.active ? '#fff' : '#333',
                        transform: pkg.active ? 'scale(1.1)' : 'scale(1)',
                        zIndex: pkg.active ? 2 : 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        border: pkg.active ? 'none' : '1px solid #eee'
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700} mb={2} sx={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: 1 }}>{pkg.plan}</Typography>
                        <Typography variant="h5" fontWeight={800} mb={4} sx={{ color: pkg.active ? '#69F0AE' : '#1A9E7F', fontSize: { xs: '1.2rem', md: '1.4rem' } }}>{pkg.price}</Typography>
                        <Box mb={4} textAlign="left">
                          {pkg.features.map((f, j) => (
                            <Typography key={j} variant="body2" sx={{ mb: 1.5, opacity: 0.8, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <span>•</span> {f}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                      <Button component={Link} to="/contact" variant={pkg.active ? "contained" : "outlined"} fullWidth sx={{ borderRadius: 50, bgcolor: pkg.active ? '#1A9E7F' : 'transparent', color: pkg.active ? '#fff' : '#034522', borderColor: '#034522', fontWeight: 700 }}>
                        Get a Quote
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        {/* Internship Section */}
        <Paper
          elevation={0}
          sx={{
            py: { xs: 6, md: 10 },
            px: { xs: 3, md: 8 },
            borderRadius: "24px",
            background: "linear-gradient(135deg, #034522 0%, #002613 100%)", // Dark Green Theme
            color: "white",
            overflow: "hidden",
            position: "relative"
          }}
        >
          <Grid
            container
            spacing={6}
            alignItems="center"
            justifyContent="space-between"
            position="relative"
            zIndex={2}
          >
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "28px", sm: "36px", md: "44px" },
                  mb: 2,
                }}
              >
                Internship Opportunities
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: 1.8,
                  mb: 4,
                  opacity: 0.9,
                  fontWeight: 300
                }}
              >
                Kickstart your professional journey with our immersive internship programs designed for aspiring tech leaders. We believe in learning by doing—working side-by-side with experienced developers on live, production-level projects. Whether you are passionate about AI, Full-Stack Development, or Data Science, our program offers a structured yet flexible environment to hone your skills, understand corporate workflows, and build a portfolio that truly sets you apart.
              </Typography>

              {/* New: Detailed Domains Section */}
              <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#69F0AE' }}>
                  Available Domains:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>💻 Full Stack Development</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>Master MERN/MEAN Stack & build scalable web apps.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>🤖 AI & Machine Learning</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>Neural Networks, NLP, & Computer Vision with Python.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>📊 Data Science</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>Big Data analysis, predictive modeling & visualization.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>🛡️ Cybersecurity</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>Network security, ethical hacking, & risk analysis.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>☁️ Cloud & DevOps</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>AWS/Azure infra, CI/CD pipelines, & Docker/Kubernetes.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>📱 Mobile App Dev</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>iOS & Android apps using Flutter & React Native.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>📡 IoT & Embedded</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Smart sensors, Arduino/Raspberry Pi & automation.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700}>🔗 Blockchain</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Smart contracts, Solidity & decentralized apps (DApps).</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={4}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
                  Program Highlights:
                </Typography>
                {[
                  "Hands-on Experience: Work on real-world client projects.",
                  "Expert Mentorship: One-on-one guidance from senior engineers.",
                  "Flexible Tracks: 10-day bootcamps to 6-month fellowships.",
                  "Career Support: Resume reviews and mock interviews.",
                  "Certification: Recognized validation of your skills.",
                  "Networking: Connect with industry professionals."
                ].map((point, i) => (
                  <Box key={i} display="flex" alignItems="center" mb={1.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1A9E7F', mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1" sx={{ fontSize: "17px" }}>{point}</Typography>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                onClick={handleApplyNow}
                size="large"
                sx={{
                  backgroundColor: "#fff",
                  color: "#034522",
                  fontWeight: 700,
                  alignSelf: "flex-start",
                  px: 5,
                  py: 1.5,
                  borderRadius: "30px",
                  "&:hover": { backgroundColor: "#f0f0f0", transform: "scale(1.05)" },
                  transition: "all 0.3s"
                }}
              >
                Apply Now
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <MotionBox
                initial={{ opacity: 0, rotate: 5 }}
                whileInView={{ opacity: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  component="img"
                  src={internshipImage}
                  alt="Internship Program"
                  sx={{
                    width: "100%",
                    maxWidth: "400px",
                    borderRadius: "20px",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                    transform: { md: "scale(1.1)" }
                  }}
                />
              </MotionBox>
            </Grid>
          </Grid>
        </Paper>

        {/* Workshop Section */}
        <Box
          sx={{
            mt: 12,
            py: 8,
            px: { xs: 2, sm: 4, md: 6 },
          }}
        >
          <Grid
            container
            spacing={6}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: "flex", justifyContent: "center", order: { xs: 2, md: 1 } }}
            >
              <MotionBox
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  component="img"
                  src={workshopImage}
                  alt="Workshop"
                  sx={{
                    width: "100%",
                    maxWidth: "450px",
                    borderRadius: "20px",
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  }}
                />
              </MotionBox>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column", order: { xs: 1, md: 2 } }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  color: '#FF9A15', // Accent color
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  mb: 1
                }}
              >
                Learn & Grow
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "28px", sm: "36px", md: "44px" },
                  color: "#1a1a1a",
                  mb: 3,
                }}
              >
                Interactive Workshops
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: 1.8,
                  mb: 4,
                  color: '#555',
                  textAlign: "left",
                }}
              >
                Our workshops bridge the critical gap between theory and practice. Led by industry veterans, these sessions provide intense, hands-on training to master modern technologies in days, not months. Whether you are a student looking to upskill or a professional aiming to pivot, our curriculum is designed to be impactful, practical, and immediately applicable.
              </Typography>

              {/* New: Workshop Topics Section */}
              <Box sx={{ mb: 4, p: 3, bgcolor: '#fff', borderRadius: 4, border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#FF9A15' }}>
                  Popular Workshop Topics:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">⚡ Generative AI</Typography>
                    <Typography variant="body2" color="text.secondary">Build LLM-powered apps & chatbots.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">🌐 Modern Web</Typography>
                    <Typography variant="body2" color="text.secondary">React, Next.js & TailwindCSS deep dives.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">📊 Data Visualization</Typography>
                    <Typography variant="body2" color="text.secondary">Mastering PowerBI, Tableau & D3.js.</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">🔒 Ethical Hacking</Typography>
                    <Typography variant="body2" color="text.secondary">Hands-on penetration testing & security.</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={4}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2, color: '#034522' }}>
                  Key Takeaways:
                </Typography>
                {[
                  "Project-Based Learning: Build a real project by the end.",
                  "Certificate of Completion: Validated by GrowAITech.",
                  "Resource Kit: Access to codebases, slides & tools.",
                  "Network: Join a community of like-minded learners.",
                ].map((point, i) => (
                  <Box key={i} display="flex" alignItems="center" mb={1.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF9A15', mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1" sx={{ fontSize: "17px", color: '#444' }}>{point}</Typography>
                  </Box>
                ))}
              </Box>

              <Button
                variant="outlined"
                onClick={handleEnrollNow}
                size="large"
                sx={{
                  borderColor: "#034522",
                  color: "#034522",
                  borderWidth: 2,
                  fontWeight: 700,
                  alignSelf: "flex-start",
                  px: 4,
                  py: 1,
                  borderRadius: "30px",
                  "&:hover": { backgroundColor: "#034522", color: "#fff", borderWidth: 2 },
                }}
              >
                Start Learning
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* 🔹 INDUSTRY SOLUTIONS - NEW SECTION */}
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
          <Container maxWidth="lg">
            <SectionHeader tagline="Sector Expertise" title="Tailored by Industry" subtitle="We understand that every industry has its own lexicon, regulations, and challenges. Our solutions are purpose-built." center />

            <Grid container spacing={4} mt={4}>
              {[
                { industry: "FinTech", desc: "High-frequency trading platforms, blockchain ledgers, and secure payment gaterways.", icon: <Business /> },
                { industry: "Healthcare", desc: "HIPAA-compliant telemedicine, patient portals, and diagnostic AI models.", icon: <School /> },
                { industry: "Retail & Commerce", desc: "Omnichannel inventory, personalized recommendation engines, and headless commerce.", icon: <Groups /> },
                { industry: "Manufacturing", desc: "Predictive maintenance, digital twins, and supply chain automation via IoT.", icon: <WorkOutline /> }
              ].map((ind, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Paper elevation={0} sx={{ p: 4, display: 'flex', gap: 3, alignItems: 'start', borderRadius: 4, bgcolor: '#F0F9F6', border: '1px solid #E0F2F1' }}>
                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: '50%', color: '#034522', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      {ind.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="#034522" gutterBottom>{ind.industry}</Typography>
                      <Typography variant="body1" color="text.secondary">{ind.desc}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* 🔹 TECHNOLOGY STACK - NEW SECTION */}
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#FAFAFA', borderRadius: 8, mb: 12 }}>
          <Container maxWidth="lg">
            <Box textAlign="center" mb={8}>
              <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Our Toolbox</Typography>
              <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>Technologies We Master</Typography>
            </Box>
            <Grid container spacing={2}>
              {[
                { domain: "Frontend", techs: ["React", "Next.js", "Vue", "Angular", "Tailwind"] },
                { domain: "Backend", techs: ["Node.js", "Python", "Go", "Java", ".NET"] },
                { domain: "Mobile", techs: ["Flutter", "React Native", "Swift", "Kotlin"] },
                { domain: "Cloud & DevOps", techs: ["AWS", "Azure", "Docker", "Kubernetes", "Terraform"] },
                { domain: "Data & AI", techs: ["TensorFlow", "PyTorch", "Spark", "Kafka", "Snowflake"] },
                { domain: "Database", techs: ["PostgreSQL", "MongoDB", "Redis", "Elasticsearch"] }
              ].map((stack, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 3, height: '100%' }}>
                    <Typography variant="h6" fontWeight={700} color="#034522" mb={2}>{stack.domain}</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {stack.techs.map((t, j) => (
                        <Box key={j} sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>
                          {t}
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Achievements Section */}
        <Box sx={{ mt: 12, mb: 8, bgcolor: "#f8f9fa", borderRadius: 8, py: { xs: 6, md: 10 } }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "26px", sm: "36px", md: "42px" },
              textAlign: "center",
              color: "#1a1a1a",
              mb: 8,
            }}
          >
            Our Impact in Numbers
          </Typography>
          <Grid container spacing={4} justifyContent="center" px={4}>
            {[
              {
                label: "Completed Projects",
                end: 100,
                suffix: "+",
                color: "#e3f2fd",
                textColor: "#1565c0",
                icon: <WorkOutline sx={{ fontSize: 40, mb: 1, color: "#1565c0" }} />,
              },
              {
                label: "Happy Clients",
                end: 45,
                suffix: "+",
                color: "#fff3e0",
                textColor: "#ef6c00",
                icon: <Groups sx={{ fontSize: 40, mb: 1, color: "#ef6c00" }} />,
              },
              {
                label: "Training Sessions",
                end: 200,
                suffix: "+",
                color: "#f3e5f5",
                textColor: "#8e24aa",
                icon: <School sx={{ fontSize: 40, mb: 1, color: "#8e24aa" }} />,
              },
              {
                label: "Academic Partners",
                end: 15,
                suffix: "+",
                color: "#e8f5e9",
                textColor: "#2e7d32",
                icon: <Business sx={{ fontSize: 40, mb: 1, color: "#2e7d32" }} />,
              },
            ].map((stat, i) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={i}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  sx={{
                    width: "100%",
                    maxWidth: "240px",
                    borderRadius: "20px",
                    background: "white",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                    p: 4,
                    textAlign: "center",
                    transition: "transform 0.3s ease",
                    "&:hover": { transform: "translateY(-10px)", boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "50%",
                      bgcolor: stat.color,
                      mb: 2,
                      display: 'flex'
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "28px", sm: "36px" },
                      color: stat.textColor
                    }}
                  >
                    <CountUp end={stat.end} duration={2.5} />
                    {stat.suffix}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      mt: 1,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#666"
                    }}
                  >
                    {stat.label}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default Services;
